import React, { useEffect, useState, useMemo } from "react";
import useNivoLine from "../../charts/nivo/useNivoLine";
import NivoLineController from "../../charts/nivo/NivoLineController";
import { COOL_COLOR_PALETTE } from "../../utils/colorUtils";

const EtfCumulativeLineChart = ({ etfs }) => {
  const maxHoldingsLength = Math.max(
    ...etfs.map((etf) => etf.holdings.length)
  );

  // Manage chart dimensions at ~70vh and ~90% width
  const [chartDimensions, setChartDimensions] = useState({
    width: window.innerWidth * 0.9,
    height: window.innerHeight * 0.7,
  });

  // Handle resizing
  useEffect(() => {
    const handleResize = () => {
      setChartDimensions({
        width: window.innerWidth * 0.9,
        height: window.innerHeight * 0.7,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prepare data for Nivo
  const data = useMemo(() => {
    return etfs.map((etf, idx) => {
      let cumulativeWeight = 0;
      return {
        id: etf.name,
        color: COOL_COLOR_PALETTE[idx % COOL_COLOR_PALETTE.length],
        data: etf.holdings.map((holding, index) => {
          cumulativeWeight += holding.wgt;
          return {
            x: index + 1,           // we label x as 1-based
            y: cumulativeWeight * 100,
          };
        }),
      };
    });
  }, [etfs]);

  // Markers: one dashed vertical line at the last x = holdings.length of each ETF
  const markers = useMemo(() => {
    return etfs.map((etf, idx) => ({
      axis: "x",
      value: etf.holdings.length, // where the ETF ends
      lineStyle: {
        stroke: COOL_COLOR_PALETTE[idx % COOL_COLOR_PALETTE.length],
        strokeWidth: 2,
        strokeDasharray: "6 6",
      },
      legend: etf.name,
      legendPosition: "top",
    }));
  }, [etfs]);

  // Use the custom hook for line chart config
  const { chartData, config, setConfig } = useNivoLine({
    data,
    // Keep the x scale from 1..maxHoldingsLength
    xScale: { type: "linear", min: 1, max: maxHoldingsLength },
    // Keep the y scale from 0..100 (cumulative 100%)
    yScale: { type: "linear", min: 0, max: 100 },
    isArea: false,
    pointSize: 1,
    enablePoints: false,
  });

  // Extend/override config for smooth lines + custom markers
  if (config) {
    config.curve = "monotoneX";   // makes the lines smooth
    config.markers = markers;     // add dashed vertical lines
  }

  return (
    <div
      style={{
        width: "100%",
        height: "70vh", // or you can use chartDimensions.height if you prefer
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <NivoLineController
        data={chartData}
        xScale={config?.xScale}
        yScale={config?.yScale}
        config={config}
        setConfig={setConfig}
        // Use 90% width of our container
        width={chartDimensions.width * 0.9}
        height={chartDimensions.height}
      />
    </div>
  );
};

export default EtfCumulativeLineChart;
